import { useEffect, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useBreadcrumb } from "@accredo/fe-shared/contexts/BreadcrumbContext.jsx";
import { useUser } from "@accredo/fe-shared/hooks/user.js";
import TabList from "@accredo/fe-shared/components/TabList.jsx";
import PersonalDetailsSection from "./components/PersonalDetailsSection.jsx";
import NotificationSettingsSection from "./components/NotificationSettingsSection.jsx";
import DisplaySettingsSection from "./components/DisplaySettingsSection.jsx";
import SecuritySettingsSection from "./components/SecuritySettingsSection.jsx";

/**
 * User Settings Page (v2)
 * Provides tabbed interface for user account settings
 */
export default function UserAccountSettingsPage() {
  const { section } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setBreadcrumbs } = useBreadcrumb();
  const { user, loading: userLoading, setUser } = useUser();

  const activeSection = section || "personal";

  // Define tabs configuration
  const tabs = useMemo(
    () => [
      {
        key: "personal",
        label: "Persönliche Angaben",
        content: <PersonalDetailsSection userData={user} setUser={setUser} />,
      },
      {
        key: "notifications",
        label: "Benachrichtigungen",
        content: (
          <NotificationSettingsSection userData={user} setUser={setUser} />
        ),
      },
      {
        key: "display",
        label: "Anzeigeeinstellungen",
        content: <DisplaySettingsSection userData={user} setUser={setUser} />,
      },
      {
        key: "security",
        label: "Sicherheit",
        content: <SecuritySettingsSection />,
      },
    ],
    [user, setUser]
  );

  // Redirect to default section if none specified
  useEffect(() => {
    if (!section) {
      navigate("/account/personal", { replace: true });
    }
  }, [section, navigate]);

  // Update breadcrumbs
  useEffect(() => {
    const activeTab = tabs.find((tab) => tab.key === activeSection);
    const breadcrumbs = [
      { name: "Home", path: "/home" },
      { name: "Meine Benutzerdaten", path: "/account" },
    ];

    if (activeTab) {
      breadcrumbs.push({
        name: activeTab.label,
        path: `/account/${activeSection}`,
      });
    }

    setBreadcrumbs(breadcrumbs);
  }, [activeSection, tabs, setBreadcrumbs]);

  const handleTabChange = (tabKey) => {
    navigate(`/account/${tabKey}`);
  };

  if (userLoading || !user) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          minHeight: "60vh",
        }}
      >
        <CircularProgress color="primary" size={48} />
        <Typography variant="body1" color="text.secondary">
          Lade Benutzerdaten...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        pt: 0,
        pb: 6,
      }}
    >
      <Box>
        <Typography variant="h1" sx={{ mb: 1 }}>
          Meine Benutzerdaten
        </Typography>
      </Box>

      <TabList
        tabs={tabs}
        activeTab={activeSection}
        onTabChange={handleTabChange}
        renderContent={true}
        contentWrapperSx={{ mt: 4 }}
      />
    </Box>
  );
}
